import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiPlus, FiEdit2, FiTrash2, FiX, FiLayers, FiImage } from 'react-icons/fi';
import { getCollections, addCollection, updateCollection, deleteCollection } from '../../firebase/database';
import { uploadCollectionImage } from '../../firebase/storage';
import { compressImage } from '../../utils/imageCompress';
import { Spinner } from '../../components/common/Loader';
import toast from 'react-hot-toast';

const emptyCol = { name: '', subtitle: '', description: '', image: '', slug: '', color: '#1a3a6e', featured: false, productCategories: [], order: 0 };
const PRODUCT_CATS = ['plastic-chairs', 'office-chairs', 'baby-chairs', 'tables', 'dining-sets', 'outdoor'];

const AdminCollections = () => {
  const [collections, setCollections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyCol);
  const [saving, setSaving] = useState(false);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [uploadProgress, setUploadProgress] = useState(0);

  useEffect(() => { load(); }, []);
  const load = async () => { setLoading(true); const data = await getCollections(); setCollections(data); setLoading(false); };

  const openAdd = () => { setEditing(null); setForm({ ...emptyCol, order: collections.length }); setImageFile(null); setImagePreview(''); setModal(true); };
  const openEdit = (col) => { setEditing(col.id); setForm(col); setImagePreview(col.image || ''); setImageFile(null); setModal(true); };

  const toggleCategory = (cat) => {
    setForm(f => ({
      ...f,
      productCategories: f.productCategories?.includes(cat)
        ? f.productCategories.filter(c => c !== cat)
        : [...(f.productCategories || []), cat]
    }));
  };

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error('Name required'); return; }
    setSaving(true);
    try {
      let imageUrl = form.image || '';
      if (imageFile) {
        const colId = editing || `col_${Date.now()}`;
        const compressedFile = await compressImage(imageFile);
        imageUrl = await uploadCollectionImage(compressedFile, colId);
      }
      const data = {
        ...form,
        image: imageUrl,
        slug: form.slug || form.name.toLowerCase().replace(/\s+/g, '-'),
        updatedAt: Date.now()
      };
      if (editing) { await updateCollection(editing, data); toast.success('Collection updated!'); }
      else { await addCollection(data); toast.success('Collection added!'); }
      setModal(false); load();
    } catch (err) { toast.error('Failed: ' + err.message); } finally { setSaving(false); setUploadProgress(0); }
  };

  const handleDelete = async (id) => {
    if (!id) { toast.error('Invalid collection — cannot delete'); return; }
    if (!window.confirm('Delete this collection?')) return;
    try {
      await deleteCollection(id);
      toast.success('Deleted');
      load();
    } catch (err) {
      console.error('Delete collection failed:', err);
      toast.error('Failed to delete: ' + (err.message || 'Check your admin permissions.'));
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-display text-2xl font-black text-gray-900">Collections</h1>
          <p className="text-gray-500 text-sm">{collections.length} curated collections</p>
        </div>
        <button onClick={openAdd} className="btn-primary flex items-center gap-2 text-sm"><FiPlus className="w-4 h-4" /> Add Collection</button>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {loading ? Array(3).fill(0).map((_, i) => <div key={i} className="skeleton h-48 rounded" />) : (
          collections.map((col, i) => (
            <motion.div key={col.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.08 }} className="bg-white shadow-card overflow-hidden group">
              <div className="relative h-36 overflow-hidden">
                {col.image ? (
                  <img src={col.image} alt={col.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" onError={e => e.target.src = 'https://via.placeholder.com/400x144?text=' + col.name} />
                ) : (
                  <div className="w-full h-full flex items-center justify-center" style={{ background: col.color || '#1a3a6e' }}>
                    <FiLayers className="w-12 h-12 text-white/50" />
                  </div>
                )}
                {col.featured && <span className="absolute top-2 left-2 bg-[#e63946] text-white text-xs font-bold px-2 py-0.5">Featured</span>}
              </div>
              <div className="p-4">
                <h3 className="font-bold text-gray-900">{col.name}</h3>
                <p className="text-xs text-gray-500 mt-0.5">{col.subtitle}</p>
                <p className="text-xs text-gray-400 mt-1 line-clamp-2">{col.description}</p>
                {col.productCategories?.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-2">
                    {col.productCategories.map(c => (
                      <span key={c} className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5">{c}</span>
                    ))}
                  </div>
                )}
                <div className="flex gap-2 mt-3 border-t pt-3">
                  <button onClick={() => openEdit(col)} className="flex-1 flex items-center justify-center gap-1.5 py-1.5 text-xs font-semibold text-[#1a3a6e] border border-[#1a3a6e]/30 hover:bg-[#1a3a6e]/5 transition-colors">
                    <FiEdit2 className="w-3 h-3" /> Edit
                  </button>
                  <button onClick={() => handleDelete(col.id)} className="flex-1 flex items-center justify-center gap-1.5 py-1.5 text-xs font-semibold text-red-500 border border-red-200 hover:bg-red-50 transition-colors">
                    <FiTrash2 className="w-3 h-3" /> Delete
                  </button>
                </div>
              </div>
            </motion.div>
          ))
        )}
        {!loading && collections.length === 0 && (
          <div className="col-span-3 text-center py-16 text-gray-400">
            <FiLayers className="w-12 h-12 mx-auto mb-3" /><p>No collections yet. Create your first!</p>
          </div>
        )}
      </div>

      {/* Modal */}
      <AnimatePresence>
        {modal && (
          <>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-black/50 z-50" onClick={() => setModal(false)} />
            <motion.div initial={{ opacity: 0, scale: 0.95, y: 20 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.95 }} className="fixed inset-0 flex items-center justify-center z-50 p-4">
              <div className="bg-white w-full max-w-xl shadow-2xl max-h-[90vh] overflow-y-auto">
                <div className="flex justify-between items-center p-5 border-b sticky top-0 bg-white z-10">
                  <h2 className="font-bold text-lg">{editing ? 'Edit' : 'Add'} Collection</h2>
                  <button onClick={() => setModal(false)}><FiX className="w-5 h-5" /></button>
                </div>
                <div className="p-5 space-y-4">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-bold text-gray-700 mb-1.5">Name *</label>
                      <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value, slug: e.target.value.toLowerCase().replace(/\s+/g, '-') }))} className="input-field" placeholder="Living Room" />
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-gray-700 mb-1.5">Subtitle</label>
                      <input value={form.subtitle} onChange={e => setForm(f => ({ ...f, subtitle: e.target.value }))} className="input-field" placeholder="Comfort Meets Style" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-gray-700 mb-1.5">Description</label>
                    <textarea rows={3} value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} className="input-field resize-none" placeholder="Short collection description..." />
                  </div>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-bold text-gray-700 mb-1.5">Slug (URL)</label>
                      <input value={form.slug} onChange={e => setForm(f => ({ ...f, slug: e.target.value }))} className="input-field" placeholder="living-room" />
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-gray-700 mb-1.5">Display Order</label>
                      <input type="number" value={form.order} onChange={e => setForm(f => ({ ...f, order: +e.target.value }))} className="input-field" />
                    </div>
                  </div>
                  {/* Image Upload */}
                  <div>
                    <label className="block text-xs font-bold text-gray-700 mb-1.5">Cover Image</label>
                    <label className="flex items-center gap-3 border-2 border-dashed border-gray-300 p-4 cursor-pointer hover:border-[#1a3a6e] transition-colors">
                      <FiImage className="w-5 h-5 text-gray-400" />
                      <span className="text-sm text-gray-500">{imageFile ? imageFile.name : 'Click to upload image'}</span>
                      <input type="file" accept="image/*" className="hidden" onChange={e => { const f = e.target.files[0]; if (f) { setImageFile(f); setImagePreview(URL.createObjectURL(f)); } }} />
                    </label>
                    {imagePreview && <img src={imagePreview} alt="Preview" className="mt-2 h-24 object-cover w-full" />}
                  </div>
                  {/* Product Categories */}
                  <div>
                    <label className="block text-xs font-bold text-gray-700 mb-2">Product Categories (linked products)</label>
                    <div className="flex flex-wrap gap-2">
                      {PRODUCT_CATS.map(cat => (
                        <button key={cat} type="button" onClick={() => toggleCategory(cat)} className={`px-3 py-1.5 text-xs font-semibold transition-all ${form.productCategories?.includes(cat) ? 'bg-[#1a3a6e] text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
                          {cat}
                        </button>
                      ))}
                    </div>
                  </div>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={form.featured} onChange={e => setForm(f => ({ ...f, featured: e.target.checked }))} className="custom-check" />
                    <span className="text-sm font-medium text-gray-700">Show in Featured Collections</span>
                  </label>
                </div>
                <div className="flex justify-end gap-3 p-5 border-t sticky bottom-0 bg-white">
                  <button onClick={() => setModal(false)} className="btn-outline text-sm py-2 px-5">Cancel</button>
                  <button onClick={handleSave} disabled={saving} className="btn-primary text-sm py-2 px-5 flex items-center gap-2">
                    {saving && <Spinner size="sm" color="white" />}
                    {editing ? 'Update' : 'Add'} Collection
                  </button>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AdminCollections;
